import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Button, Skeleton, Switch, ToggleButtonGroup, ToggleButton, TextField, CircularProgress } from '@mui/material';
import AgentKPICards from './AgentKPICards';
import LoadingScreen from '../../components/LoadingScreen';
import socket from '../../socket';
import { useSnackbar } from '../../utils/SnackbarProvider';
import AgentPerformanceChart from './AgentPerformanceChart';
import AgentRecentOrdersTable from './AgentRecentOrdersTable';
import AgentCommissionSummary from './AgentCommissionSummary';
import AgentSponsorshipCard from './AgentSponsorshipCard';
import api from '../../api/axios';
import {
  requestNotificationPermission,
  getNotificationPermission,
  getPushEnabled,
  setPushEnabled,
  registerServiceWorker,
  subscribeToPush,
  getNotificationStatus,
  reRegisterServiceWorker,
} from '../../utils/pushNotification';
import { useNotificationTabCoordinator } from '../../utils/notificationTabCoordinator';
import { useNotificationSocketHandler } from '../../utils/useNotificationSocketHandler';

function formatCurrency(value) {
  if (typeof value === 'number') return `₹${value.toLocaleString('en-IN')}`;
  if (value === 0) return '₹0';
  return value || '—';
}

function timeAgo(timestamp) {
  if (!timestamp) return 'Just now';
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function calculateItemCommission(item) {
  if (!item) return 0;
  let commissionRate = item.commissionRate;
  const commissionType = item.commissionType;
  if (commissionRate === undefined || commissionRate === null || commissionType === undefined) return 0;
  commissionRate = Number(commissionRate);
  if (isNaN(commissionRate)) return 0;
  const baseAmount = (item.price || 0) * (item.qty || 1);
  if (commissionType === 'percentage') return Number((baseAmount * commissionRate).toFixed(2));
  return Number(commissionRate.toFixed(2));
}

function calculateOrderCommission(order) {
  if (!order.items || order.items.length === 0) return Number(order.agentCommission?.amount ?? 0);
  return order.items.reduce((sum, item) => sum + calculateItemCommission(item), 0);
}

/* ── Availability Toggle Component ── */
function AgentAvailabilityToggle() {
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    api.get('/agent/dashboard').then(({ data }) => {
      if (data?.profile?.isAvailable !== undefined) setIsAvailable(data.profile.isAvailable);
    }).catch(() => {});
  }, []);

  const handleToggle = useCallback(async () => {
    const previousState = isAvailable;
    setIsAvailable(!isAvailable);
    setLoading(true);
    try {
      const res = await api.put('/agent/availability', { isAvailable: !previousState });
      const data = res.data;
      if (data.success) { setIsAvailable(data.isAvailable); showSnackbar(data.message, 'success'); }
      else { setIsAvailable(previousState); showSnackbar(data.message || 'Failed to update availability', 'error'); }
    } catch (err) {
      setIsAvailable(previousState);
      console.error('Toggle availability error:', err?.response?.data || err.message || err);
      showSnackbar(err?.response?.data?.message || 'Failed to update availability', 'error');
    } finally { setLoading(false); }
  }, [isAvailable, showSnackbar]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: isAvailable ? '#4ade80' : 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isAvailable ? 'Active' : 'Inactive'}</Typography>
      <Switch checked={isAvailable} onChange={handleToggle} disabled={loading} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ade80' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4ade80' }, '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' }, '& .MuiSwitch-thumb': { backgroundColor: '#fff' } }} />
    </Box>
  );
}

function buildTrendData(orders) {
  const now = new Date();
  const buildRange = (days) => {
    const buckets = Array.from({ length: days }).map((_, index) => {
      const date = new Date(now);
      date.setDate(now.getDate() - (days - index - 1));
      return {
        date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        completed: 0,
        earnings: 0,
      };
    });
    const bucketMap = new Map(buckets.map((b) => [b.date, b]));
    orders.forEach((order) => {
      if (order.deliveryPhase?.status !== 'DELIVERED') return;
      const createdAt = new Date(order.createdAt || order.agent?.assignedAt || order.updatedAt || now);
      const label = createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
      const bucket = bucketMap.get(label);
      if (bucket) {
        bucket.completed += 1;
        bucket.earnings += calculateOrderCommission(order);
      }
    });
    return buckets;
  };
  return { today: buildRange(1), '7d': buildRange(7), '30d': buildRange(30), '90d': buildRange(90) };
}

/* ── Date range helpers ── */
function isOrderInRange(order, range, customStart, customEnd) {
  if (order.deliveryPhase?.status !== 'DELIVERED') return false;
  const dt = new Date(order.deliveryPhase?.deliveredAt || order.createdAt || order.agent?.assignedAt || order.updatedAt || Date.now());
  const now = new Date();
  const day = 86400000;
  switch (range) {
    case 'today': return dt.toDateString() === now.toDateString();
    case '7d': return (now - dt) / day <= 7;
    case '30d': return (now - dt) / day <= 30;
    case '90d': return (now - dt) / day <= 90;
    case 'custom':
      if (customStart && dt < customStart) return false;
      if (customEnd) {
        const endOfDay = new Date(customEnd);
        endOfDay.setHours(23, 59, 59, 999);
        if (dt > endOfDay) return false;
      }
      return true;
    default: return true;
  }
}

function calculateKPIs(allOrders, profile, pendingOrders, kpiRange, customStart, customEnd, notificationStats) {
  const normalizeStatus = (status) => String(status || '').toUpperCase();
  const isCompletedStatus = (status) => ['COMPLETED', 'DELIVERED'].includes(normalizeStatus(status));
  const startDate = customStart ? new Date(customStart) : null;
  const endDate = customEnd ? new Date(customEnd) : null;

  const filteredForRange = allOrders.filter(o => isOrderInRange(o, kpiRange, startDate, endDate));
  const acceptedOrders = allOrders.filter((order) => !isCompletedStatus(order.deliveryPhase?.status));
  const completedOrders = filteredForRange.filter((order) => isCompletedStatus(order.deliveryPhase?.status));
  const rangeCommission = completedOrders.reduce((sum, order) => sum + calculateOrderCommission(order), 0);

  const stats = notificationStats || { accepted: 0, declined: 0, expired: 0 };
  const totalNotifs = stats.accepted + stats.declined + stats.expired;
  const acceptanceRate = totalNotifs > 0 ? Math.round((stats.accepted / totalNotifs) * 100) : 0;

  let totalAcceptMs = 0;
  let acceptCount = 0;
  filteredForRange.forEach(order => {
    const notif = order.agentNotifications?.find(n => n.status === "ACCEPTED" && n.sentAt && n.acceptedAt);
    if (!notif) return;
    const diff = new Date(notif.acceptedAt) - new Date(notif.sentAt);
    if (diff > 0) { totalAcceptMs += diff; acceptCount++; }
  });
  let avgAcceptTime = '—';
  if (acceptCount > 0) {
    const avgSec = Math.round(totalAcceptMs / acceptCount / 1000);
    if (avgSec < 60) avgAcceptTime = `${avgSec}s`;
    else if (avgSec < 3600) avgAcceptTime = `${Math.floor(avgSec / 60)}m ${avgSec % 60}s`;
    else avgAcceptTime = `${Math.floor(avgSec / 3600)}h ${Math.floor((avgSec % 3600) / 60)}m`;
  }

  let totalDeliveryMs = 0;
  let deliveryCount = 0;
  completedOrders.forEach(order => {
    if (!order.agent?.assignedAt || !order.deliveryPhase?.deliveredAt) return;
    const diff = new Date(order.deliveryPhase.deliveredAt) - new Date(order.agent.assignedAt);
    if (diff > 0) { totalDeliveryMs += diff; deliveryCount++; }
  });
  let avgDeliveryTime = '—';
  if (deliveryCount > 0) {
    const avgMin = Math.round(totalDeliveryMs / deliveryCount / 60000);
    if (avgMin < 60) avgDeliveryTime = `${avgMin}m`;
    else avgDeliveryTime = `${Math.floor(avgMin / 60)}h ${avgMin % 60}m`;
  }

  const getLabel = () => {
    if (kpiRange === 'today') return 'Today';
    if (kpiRange === 'custom') {
      if (customStart && customEnd) return `${customStart} to ${customEnd}`;
      if (customStart) return `From ${customStart}`;
      return 'Custom';
    }
    return kpiRange;
  };

  const kpi = {
    available: pendingOrders.length,
    assigned: acceptedOrders.length,
    completed: completedOrders.length,
    rangeLabel: getLabel(),
    todayCommission: formatCurrency(rangeCommission),
    acceptance: acceptanceRate,
    lost: stats.expired,
    cancellationRate: totalNotifs > 0 ? Number(((stats.declined / totalNotifs) * 100).toFixed(2)) : 0,
    cancellationCounts: totalNotifs > 0 ? `${stats.declined}/${totalNotifs}` : '0/0',
    avgAcceptTime,
    avgDeliveryTime,
  };

  let todayComm = 0, weekComm = 0, monthComm = 0;
  for (const o of allOrders) {
    if (isOrderInRange(o, '30d')) {
      const c = calculateOrderCommission(o);
      monthComm += c;
      if (isOrderInRange(o, '7d')) weekComm += c;
      if (isOrderInRange(o, 'today')) todayComm += c;
    }
  }
  const commissionSummary = {
    today: formatCurrency(todayComm),
    week: formatCurrency(weekComm),
    month: formatCurrency(monthComm),
  };

  return { kpi, commissionSummary, agentId: profile?._id || null, sponsorshipStatus: profile?.sponsorshipStatus || 'NONE' };
}

export default function AgentDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [orders, setOrders] = useState([]);
  const [available, setAvailable] = useState([]);
  const [chartRange, setChartRange] = useState('7d');
  const [chartData, setChartData] = useState({ '7d': [], '30d': [], '90d': [], today: [] });
  const [notifications, setNotifications] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState({});
  const [kycStatus, setKycStatus] = useState(null);
  const [kycRejectionMessage, setKycRejectionMessage] = useState("");
  const [sponsorshipStatus, setSponsorshipStatus] = useState("NONE");
  const [sponsorshipExpiresAt, setSponsorshipExpiresAt] = useState(null);
  const [agentId, setAgentId] = useState(() => {
    try { return localStorage.getItem('agentProfileId') || null; } catch { return null; }
  });
  const [availableHighlighted, setAvailableHighlighted] = useState(false);
  const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
  const [pushEnabled, setPushEnabledState] = useState(() => getPushEnabled());
  const [agentName, setAgentName] = useState(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.name || u?.firstName || u?.username || '';
  });
  const { isNotificationOwner } = useNotificationTabCoordinator();
  const [notifStatus, setNotifStatus] = useState(null);
  const [notifChecking, setNotifChecking] = useState(false);

  const [kpiRange, setKpiRange] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const navigate = useNavigate();
  const agentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isMountedRef = useRef(true);
  const initialFetchCompleteRef = useRef(false);
  const prevPendingOrderIdsRef = useRef(new Set());
  const allOrdersRef = useRef([]);
  const allProfileRef = useRef(null);
  const allPendingOrdersRef = useRef([]);
  const notificationStatsRef = useRef({ total: 0, accepted: 0, declined: 0, expired: 0 });
  const { showSnackbar } = useSnackbar();
  const fetchLockRef = useRef({ profile: false, notifications: false, orders: false });

  const fetchNotificationsRef = useRef(null);
  const notifyNewPendingOrdersRef = useRef(null);
  notifyNewPendingOrdersRef.current = (pendingOrders) => {
    const currentIds = new Set(pendingOrders.map((o) => o.orderId));

    if (!initialFetchCompleteRef.current) {
      if (pendingOrders.length > 0) {
        setAvailableHighlighted(true);
      }
      prevPendingOrderIdsRef.current = currentIds;
      return;
    }

    const newIds = [...currentIds].filter((id) => !prevPendingOrderIdsRef.current.has(id));
    if (newIds.length > 0) {
      setAvailableHighlighted(true);
    }
    prevPendingOrderIdsRef.current = currentIds;
  };

  const handleEnablePushNotifications = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPushPermission(result);
    if (result === 'granted') {
      await registerServiceWorker();
      const sub = await subscribeToPush();
      if (sub) {
        setPushEnabled(true); setPushEnabledState(true);
        showSnackbar('Push notifications enabled!', 'success');
      } else {
        showSnackbar('Could not subscribe to push notifications. Service worker may need updating.', 'warning');
      }
    } else if (result === 'denied') {
      showSnackbar('Notification permission denied. Please enable it in browser settings.', 'warning');
    }
  }, [showSnackbar]);

  const handleDisablePushNotifications = useCallback(async () => {
    setPushEnabled(false); setPushEnabledState(false);
    showSnackbar('Push notifications disabled.', 'info');
    // Also try to unsubscribe from the browser's push manager
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        }
      }
    } catch (e) {
      // ignore
    }
  }, [showSnackbar]);

  const handleReEnablePushNotifications = useCallback(async () => {
    await registerServiceWorker();
    const sub = await subscribeToPush();
    if (sub) {
      setPushEnabled(true); setPushEnabledState(true);
      showSnackbar('Push notifications re-enabled!', 'success');
    } else {
      showSnackbar('Could not subscribe to push notifications.', 'warning');
    }
  }, [showSnackbar]);

  const updateKPIs = useCallback(() => {
    const result = calculateKPIs(allOrdersRef.current, allProfileRef.current, allPendingOrdersRef.current, kpiRange, customStart, customEnd, notificationStatsRef.current);
    setKpi(result.kpi);
    setCommissionSummary(result.commissionSummary);
    if (result.sponsorshipStatus) setSponsorshipStatus(result.sponsorshipStatus);
  }, [kpiRange, customStart, customEnd]);

  const fetchProfile = useCallback(async () => {
    if (fetchLockRef.current.profile) return;
    fetchLockRef.current.profile = true;
    try {
      const { data } = await api.get('/agent/dashboard');
      if (!isMountedRef.current) return;
      const profile = data?.profile;
      if (!profile) return;
      allProfileRef.current = profile;
      if (profile.name) setAgentName(profile.name);
      if (profile?._id) {
        localStorage.setItem('agentProfileId', profile._id);
        socket.emit('agent_join', profile._id);
        setAgentId(profile._id);
      }
      if (profile.kycStatus) setKycStatus(profile.kycStatus);
      setKycRejectionMessage(profile.kycRejectionMessage || "");
      if (profile.sponsorshipStatus) setSponsorshipStatus(profile.sponsorshipStatus);
      if (profile.sponsorshipExpiresAt) setSponsorshipExpiresAt(profile.sponsorshipExpiresAt);
    } catch (err) { console.warn('Failed to fetch profile:', err); }
    finally { fetchLockRef.current.profile = false; }
  }, []);

  const fetchNotificationStats = useCallback(async () => {
    try {
      const now = Date.now(), DAY = 86400000;
      let start, end;
      if (kpiRange === 'today') start = new Date().setHours(0,0,0,0);
      else if (kpiRange === '7d') start = now - 7 * DAY;
      else if (kpiRange === '30d') start = now - 30 * DAY;
      else if (kpiRange === '90d') start = now - 90 * DAY;
      else if (kpiRange === 'custom') {
        if (customStart) start = new Date(customStart).getTime();
        if (customEnd) end = new Date(customEnd).setHours(23,59,59,999);
      }
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get('/agent/notification-stats', { params });
      if (data.success) notificationStatsRef.current = data;
    } catch (err) { console.warn('Failed to fetch notification stats:', err); }
  }, [kpiRange, customStart, customEnd]);

  const fetchNotifications = useCallback(async () => {
    if (fetchLockRef.current.notifications) return;
    fetchLockRef.current.notifications = true;
    try {
      const { data } = await api.get('/agent/notifications/pending');
      if (!isMountedRef.current) return;
      const pendingOrders = data.notifications || [];
      allPendingOrdersRef.current = pendingOrders;
      notifyNewPendingOrdersRef.current(pendingOrders);
      setAvailable(pendingOrders.map((order) => {
        const commission = order.items && order.items.length > 0 ? calculateOrderCommission({ items: order.items }) : Number(order.agentCommission?.amount ?? order.commission ?? order.totalAmount * 0.1 ?? null);
        return { id: order.orderId, vendor: order.vendorName || 'Unknown Vendor', distance: order.distanceKm ? `${order.distanceKm.toFixed(1)} km` : 'N/A', commission: formatCurrency(commission), totalAmount: order.totalAmount != null ? formatCurrency(order.totalAmount) : null, time: timeAgo(order.sentAt) };
      }));
      setNotifications(pendingOrders.map((order) => ({ id: order.orderId, title: `New order available near ${order.vendorCity || 'your area'}`, time: timeAgo(order.sentAt), read: order.isExpired || false })));
      updateKPIs();
    } catch (err) { console.warn('Failed to fetch notifications:', err); }
    finally { fetchLockRef.current.notifications = false; }
  }, [updateKPIs]);

  fetchNotificationsRef.current = fetchNotifications;

  // Bell is handled globally by NotificationManager in Layout.js.
  // Socket handler triggers data refresh and bell via window event.
  useNotificationSocketHandler({
    role: 'agent',
    isNotificationOwner,
    onNewOrder: () => {
      window.dispatchEvent(new CustomEvent('app:play-bell'));
    },
    onOrderAssigned: () => {
      window.dispatchEvent(new CustomEvent('app:play-bell'));
    },
    updatePendingOrders: fetchNotificationsRef.current,
  });

  const getCustomerName = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return '-';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name || user.name || user.username || '-';
  };
  const getVendorName = (vendor) => { if (!vendor || typeof vendor === 'string') return '-'; return vendor.store?.businessName || '-'; };
  const getVendorPickup = (vendor) => {
    if (!vendor || typeof vendor === 'string') return null;
    const addr = vendor.store?.address;
    if (!addr) return null;
    return [addr.addressLine1, addr.city].filter(Boolean).join(', ') || null;
  };
  const getOrderVendor = (order) => { for (const item of (order.items || [])) { const v = item.vendorId; if (v && typeof v === 'object') return v; } return null; };
  const getDeliveryAddress = (order) => {
    const addr = order.address || order.shippingAddress || order.deliveryAddress;
    if (!addr) return '—';
    return [addr.addressLine1, addr.city].filter(Boolean).join(', ') || '—';
  };

  const fetchOrders = useCallback(async () => {
    if (fetchLockRef.current.orders) return;
    fetchLockRef.current.orders = true;
    try {
      const { data } = await api.get('/agent/orders/accepted');
      if (!isMountedRef.current) return;
      const allOrders = data.orders || [];
      allOrdersRef.current = allOrders;

      setOrders(allOrders.map((order) => {
        const vendor = getOrderVendor(order);
        return {
          id: order._id,
          items: (order.items || []).map((item) => ({ title: item.title || item.name || 'Item', qty: item.qty || item.quantity || 1 })),
          vendor: getVendorName(vendor),
          vendorAddress: getVendorPickup(vendor),
          customer: getCustomerName(order.userId || order.customer),
          deliveryAddress: getDeliveryAddress(order),
          value: formatCurrency(order.total),
          commission: order.deliveryPhase?.status === 'DELIVERED' ? formatCurrency(calculateOrderCommission(order)) : '₹0',
          status: order.deliveryPhase?.status || order.status || 'Unknown',
          rawAcceptedDate: order.agent?.assignedAt || order.createdAt || null,
          acceptedDate: order.agent?.assignedAt ? timeAgo(order.agent.assignedAt) : '-',
        };
      }));

      const trendData = buildTrendData(allOrders);
      setChartData(trendData);
      updateKPIs();
    } catch (err) { console.warn('Failed to fetch orders:', err); }
    finally { fetchLockRef.current.orders = false; }
  }, [updateKPIs]);

  /* ── MOUNT: fetch all data ── */
  useEffect(() => {
    isMountedRef.current = true;
    const cachedAgentId = localStorage.getItem('agentProfileId');
    if (cachedAgentId) socket.emit('agent_join', cachedAgentId);
    void requestNotificationPermission();
    // Check both backend and browser for push subscription — sync UI accordingly
    (async () => {
      try {
        const { data } = await api.get('/push/status');
        let browserHasSub = false;
        if ('serviceWorker' in navigator) {
          try {
            const reg = await navigator.serviceWorker.getRegistration('/');
            if (reg) {
              const sub = await reg.pushManager.getSubscription();
              browserHasSub = !!sub;
            }
          } catch (e) {}
        }
        const isSubscribed = data.subscribed && browserHasSub;
        setPushEnabledState(isSubscribed);
        setPushEnabled(isSubscribed);
      } catch (e) {
        // ignore
      }
    })();
    if (getNotificationPermission() === 'granted') {
      registerServiceWorker().then(() => subscribeToPush());
    }

    Promise.all([fetchProfile(), fetchNotifications(), fetchOrders(), fetchNotificationStats()]).then(() => {
      if (!isMountedRef.current) return;
      updateKPIs();
      initialFetchCompleteRef.current = true;
      setLoading(false);
    }).catch(() => {
      if (!isMountedRef.current) return;
      initialFetchCompleteRef.current = true;
      setLoading(false);
    });

    return () => {
      isMountedRef.current = false;
      const leaveId = cachedAgentId;
      if (leaveId) { socket.emit('agent_leave', leaveId); }
      localStorage.removeItem('agentProfileId');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevKpiRangeRef = useRef(kpiRange);
  useEffect(() => {
    if (prevKpiRangeRef.current !== kpiRange && kpiRange !== 'custom') {
      setChartRange(kpiRange);
    }
    prevKpiRangeRef.current = kpiRange;
  }, [kpiRange]);

  useEffect(() => {
    const handleVisibility = () => { if (document.visibilityState === 'visible') setPushPermission(getNotificationPermission()); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    if (!initialFetchCompleteRef.current) return;
    fetchNotificationStats().then(() => updateKPIs());
  }, [kpiRange, customStart, customEnd, updateKPIs, fetchNotificationStats]);

  const agentIdRef = useRef(agentId);
  agentIdRef.current = agentId;

  useEffect(() => {
    const handleConnect = () => {
      const joinId = agentIdRef.current || localStorage.getItem('agentProfileId');
      if (joinId) { socket.emit('agent_join', joinId); }
      if (initialFetchCompleteRef.current) {
        fetchProfile();
        fetchNotifications();
        fetchOrders();
      }
    };

    socket.on('connect', handleConnect);

    return () => {
      socket.off('connect', handleConnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (e, val) => { if (!val) return; setKpiRange(val); };
  const handleCustomDateChange = () => {};
  const handleOpenOrderDetails = (orderId) => { if (orderId) navigate(`/agent/orders/${orderId}`); };
  const handleKpiClick = (id) => {
    if (id === 'avgAcceptTime') { navigate('/agent/avg-accept-time'); return; }
    if (id === 'avgDeliveryTime') { navigate('/agent/avg-delivery-time'); return; }
    if (id === 'cancellationRate') { navigate('/agent/cancellation-rate'); return; }
    if (id === 'lost') { navigate('/agent/orders-lost'); return; }
    if (id === 'acceptance') { navigate('/agent/acceptance-rate'); return; }
    if (id === 'todayCommission') { navigate('/agent/commission-detail'); return; }
    if (id === 'available') { navigate('/agent/orders', { state: { activeTab: 0 } }); return; }
    if (id === 'assigned') { navigate('/agent/orders', { state: { activeTab: 1 } }); return; }
    if (id === 'completed') { navigate('/agent/orders', { state: { activeTab: 2 } }); return; }
    if (id === 'pending') { navigate('/agent/orders'); return; }
  };
  const handleAcceptOrder = async (order) => {
    setAvailable((prev) => prev.filter((o) => o.id !== order.id));
    setOrders((prev) => [{ ...order, id: order.id, customer: '-', vendor: order.vendor, value: order.totalAmount || '—', commission: order.commission || '—', status: 'Assigned', acceptedDate: new Date().toLocaleDateString() }, ...prev]);
    try { await api.post(`/agent/notifications/${order.id}/accept`); } catch (error) { console.error('Accept order failed:', error); }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', flexGrow: 1 }}>
              {(() => { const hour = new Date().getHours(); const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'; const fullName = agentName || agentUser?.name || agentUser?.firstName || agentUser?.username || ''; const name = fullName.split(' ')[0] || 'Agent'; return `👋 ${greeting}, ${name}!`; })()}
            </Typography>
            <AgentAvailabilityToggle />
            {(agentId || initialFetchCompleteRef.current) ? <AgentSponsorshipCard agentId={agentId} sponsorshipStatus={sponsorshipStatus} sponsorshipExpiresAt={sponsorshipExpiresAt} onApplied={() => { fetchProfile(); }} /> : <Skeleton variant="rectangular" width={160} height={36} sx={{ borderRadius: 2 }} />}
            {(() => {
              if (pushPermission === 'denied') {
                return (
                  <Button variant="outlined" size="small" disabled title="Enable in browser settings → Site Settings → Notifications" sx={{ color: '#ef5350', borderColor: '#ef5350', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', opacity: 0.8 }}>
                    🔕 Notifications Blocked
                  </Button>
                );
              }
              if (pushPermission === 'default' || !pushEnabled) {
                return (
                  <Button variant="outlined" size="small" onClick={handleEnablePushNotifications} disabled={notifChecking} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                    {notifChecking ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : '🔔 '}
                    {notifChecking ? 'Checking...' : pushPermission === 'default' ? 'Enable Notifications' : 'Notifications Off'}
                  </Button>
                );
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={handleDisablePushNotifications} sx={{ color: '#4ade80', borderColor: '#4ade80', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', '&:hover': { borderColor: '#22c55e', bgcolor: 'rgba(74,222,128,0.08)' } }}>
                    ✅ Notifications On
                  </Button>
                  {notifStatus && !notifStatus.working && notifStatus.actionable && (
                    <Button variant="outlined" size="small" onClick={async () => {
                      setNotifChecking(true);
                      await reRegisterServiceWorker();
                      const status = await getNotificationStatus();
                      setNotifStatus(status);
                      setNotifChecking(false);
                      showSnackbar(status.working ? 'Notifications fixed!' : 'Still not working. Try clearing browser data for this site.', status.working ? 'success' : 'warning');
                    }} disabled={notifChecking} sx={{ color: '#ff9800', borderColor: '#ff9800', textTransform: 'none', borderRadius: '20px', fontSize: '0.7rem', '&:hover': { borderColor: '#f57c00', bgcolor: 'rgba(255,152,0,0.08)' } }}>
                      {notifChecking ? <CircularProgress size={14} sx={{ color: '#ff9800' }} /> : '⚠️ Fix Notifications'}
                    </Button>
                  )}
                </Box>
              );
            })()}
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Manage orders, track earnings, and monitor your performance.</Typography>
        </Box>

      {kycStatus && kycStatus !== "verified" && (
        <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 1.5,
            ...(kycStatus === "pending" && { bgcolor: "#e3f2fd", color: "#1565c0" }),
            ...(kycStatus === "submitted" && { bgcolor: "#fff8e1", color: "#b26a00" }),
            ...(kycStatus === "rejected" && { bgcolor: "#fdecea", color: "#b71c1c" }),
          }}>
            <Typography sx={{ fontSize: "1.25rem", lineHeight: 1, mt: 0.25 }}>
              {kycStatus === "pending" && "ℹ️"}
              {kycStatus === "submitted" && "⏳"}
              {kycStatus === "rejected" && "❌"}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {kycStatus === "pending" && "KYC Verification Required"}
                {kycStatus === "submitted" && "KYC Under Review"}
                {kycStatus === "rejected" && "Urgent Action Required"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: "block", mt: 0.25 }}>
                {kycStatus === "pending" && "Complete your profile and submit documents for KYC verification to start receiving orders."}
                {kycStatus === "submitted" && "Your documents are under review. You'll be able to receive orders once verified by the admin."}
                {kycStatus === "rejected" && "Your KYC was rejected. Please update your documents and resubmit for verification."}
              </Typography>
              {kycStatus === "rejected" && kycRejectionMessage && (
                <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.7)", border: "1px solid rgba(183, 28, 28, 0.16)" }}>
                  <Typography variant="subtitle2" fontWeight={700}>Admin message</Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: "pre-line" }}>{kycRejectionMessage}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
          <LoadingScreen message="Loading your dashboard..." />
        </Box>
      ) : (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2, p: 1.5, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#64748b', mr: 0.5 }}>📅 Period:</Typography>
            <ToggleButtonGroup size="small" value={kpiRange} exclusive onChange={handleRangeChange} sx={{ '& .MuiToggleButton-root': { fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', px: 1.5, py: 0.3, '&.Mui-selected': { bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } } } }}>
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="7d">7 Days</ToggleButton>
              <ToggleButton value="30d">30 Days</ToggleButton>
              <ToggleButton value="90d">90 Days</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            {kpiRange === 'custom' && (
              <>
                <TextField type="date" size="small" label="From" value={customStart} onChange={(e) => setCustomStart(e.target.value)} onBlur={handleCustomDateChange} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 160, '& input': { fontSize: '0.8rem', py: 0.8 } }} />
                <TextField type="date" size="small" label="To" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} onBlur={handleCustomDateChange} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 160, '& input': { fontSize: '0.8rem', py: 0.8 } }} />
              </>
            )}
          </Box>

          <AgentKPICards data={kpi} loading={loading} onCardClick={handleKpiClick} highlightId={availableHighlighted ? 'available' : null} onHighlightClick={() => setAvailableHighlighted(false)} />

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0, mt: 2 }}>
                <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>Recent Orders</Typography>
              </Box>
              <AgentRecentOrdersTable orders={orders} onView={(o) => handleOpenOrderDetails(o.id)} onTrack={(o) => handleOpenOrderDetails(o.id)} />
            </Grid>
          </Grid>
          <Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#6a4bd8' }} />
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>Performance Overview</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                  <AgentPerformanceChart data={chartData} range={chartRange} onRangeChange={setChartRange} />
                </Box>
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 200px' }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <AgentCommissionSummary summary={commissionSummary} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      </Box>
  );
}